<template>
    <nav class="flex items-center justify-between bg-white h-14 px-5 border-b border-gray-200 fixed top-0 left-0 right-0 z-[1000]">
        <!-- Logo et bienvenue -->
        <div class="flex items-center gap-4">
            <router-link to="/dashboard">
                <img src="../assets/images/sellhub-logo.png" alt="Logo" class="h-7 w-auto">
            </router-link>

            <div class="flex flex-col items-start">
                <span class="text-xs text-gray-500">Bienvenue</span>
                <span class="text-sm font-semibold text-gray-800">{{ userName }}</span>
            </div>
        </div>

        <!-- Barre de recherche -->
        <div class="flex items-center bg-gray-50 border border-gray-200 rounded-lg px-4 py-2 flex-1 mx-8 max-w-[480px]">
            <BaseInput
                v-model="searchQuery"
                type="search"
                placeholder="Rechercher un produit..."
                @keyup.enter="handleSearch"
                class="flex-1 border-0 bg-transparent focus:ring-0 p-0"
            >
                <template #icon-left>
                    <i class="fa-solid fa-search text-gray-400"></i>
                </template>
            </BaseInput>
            <BaseButton
                @click="handleSearch"
                class="ml-2 p-1.5 bg-primary hover:bg-primary-dark text-white rounded-lg transition-colors"
            >
                <i class="fa-solid fa-search text-white text-sm"></i>
            </BaseButton>
        </div>

        <!-- Actions utilisateur -->
        <div class="flex items-center gap-4">
            <!-- Notifications -->
            <div class="relative">
                <NotificationPanel />
            </div>

            <!-- Menu utilisateur -->
            <div class="relative">
                <div
                    @click.stop="toggleUserMenu"
                    class="flex items-center gap-2 cursor-pointer px-2.5 py-1.5 rounded-lg hover:bg-gray-50 transition-colors"
                >
                    <!-- Avatar -->
                    <div class="relative">
                        <img
                            v-if="userAvatar"
                            :src="userAvatar"
                            alt="Avatar"
                            class="w-8 h-8 rounded-full object-cover border border-gray-200"
                        />
                        <div
                            v-else
                            class="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-600 font-semibold text-xs"
                        >
                            {{ userInitials }}
                        </div>
                        <div class="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 bg-green-500 border-2 border-white rounded-full"></div>
                    </div>

                    <span class="text-sm font-medium text-gray-700">{{ userName }}</span>
                    <i
                        class="fa-solid fa-chevron-down text-gray-400 text-xs"
                        :class="['transition-transform duration-200', showUserMenu ? 'rotate-180' : '']"
                    ></i>
                </div>

                <!-- Dropdown menu -->
                <transition
                    enter-active-class="transition ease-out duration-100"
                    enter-from-class="transform opacity-0 scale-95"
                    enter-to-class="transform opacity-100 scale-100"
                    leave-active-class="transition ease-in duration-75"
                    leave-from-class="transform opacity-100 scale-100"
                    leave-to-class="transform opacity-0 scale-95"
                >
                    <div
                        v-if="showUserMenu"
                        v-click-outside="closeUserMenu"
                        class="absolute right-0 mt-2 w-52 bg-white rounded-lg border border-gray-200 overflow-hidden z-50"
                    >
                        <router-link
                            to="/profile"
                            class="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors no-underline"
                            @click="showUserMenu = false"
                        >
                            <i class="fa-solid fa-user text-gray-400 text-sm"></i>
                            <span>Mon profil</span>
                        </router-link>

                        <router-link
                            to="/settings"
                            class="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors no-underline"
                            @click="showUserMenu = false"
                        >
                            <i class="fa-solid fa-cog text-gray-400 text-sm"></i>
                            <span>Parametres</span>
                        </router-link>

                        <div class="h-px bg-gray-100 my-1"></div>

                        <BaseButton
                            @click="showLogoutModal = true"
                            class="w-full flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-red-50 transition-colors border-0 bg-transparent cursor-pointer text-left"
                        >
                            <i class="fa-solid fa-right-from-bracket text-red-500 text-sm"></i>
                            <span class="text-red-500">Se deconnecter</span>
                        </BaseButton>
                    </div>
                </transition>
            </div>
        </div>
    </nav>

    <!-- Modal de confirmation de deconnexion -->
    <BaseModal
        v-model="showLogoutModal"
        title="Confirmation de deconnexion"
        size="sm"
        show-cancel-button
        show-confirm-button
        cancel-text="Annuler"
        confirm-text="Se deconnecter"
        confirm-variant="danger"
        :confirm-loading="logoutLoading"
        @confirm="confirmLogout"
        @cancel="showLogoutModal = false"
    >
        <div class="text-center py-4">
            <div class="w-14 h-14 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <i class="fa-solid fa-right-from-bracket text-red-500 text-2xl"></i>
            </div>
            <p class="text-gray-600 text-sm">Etes-vous sur de vouloir vous deconnecter ?</p>
        </div>
    </BaseModal>
</template>

<script>
import { authService } from '@/services';
import NotificationPanel from '@/views/notifications/NotificationPanel.vue';
import CashRegisterService from '@/services/cashRegisterService';
import { useAuthStore } from '@/store/modules/auth';

export default {
    name: 'NavBar',
    components: {
        NotificationPanel
    },
    directives: {
        clickOutside: {
            mounted(el, binding) {
                el.clickOutsideEvent = function(event) {
                    if (!(el === event.target || el.contains(event.target))) {
                        binding.value(event);
                    }
                };
                document.body.addEventListener('click', el.clickOutsideEvent);
            },
            unmounted(el) {
                document.body.removeEventListener('click', el.clickOutsideEvent);
            }
        }
    },
    data() {
        return {
            searchQuery: '',
            showUserMenu: false,
            userName: '',
            userAvatar: null,
            showLogoutModal: false,
            logoutLoading: false,
            cashRegister: null
        }
    },
    computed: {
        userInitials() {
            if (!this.userName) return '?';
            return this.userName
                .split(' ')
                .map(name => name.charAt(0))
                .join('')
                .toUpperCase();
        }
    },
    async created() {
        try {
            if (CashRegisterService.hasOpenedCashRegister()) {
                const cashRegister = CashRegisterService.getOpenedCashRegister();
                this.cashRegister = cashRegister;
            }
            const userInfo = await authService.getUser();
            this.userName = userInfo.name || 'Utilisateur';
        } catch (error) {
            console.error('Erreur lors de la recuperation des informations utilisateur:', error);
        }
    },
    methods: {
        async confirmLogout() {
            this.logoutLoading = true;
            try {
                await useAuthStore().logout();
                this.showLogoutModal = false;
                this.showUserMenu = false;

                this.$nextTick(() => {
                    this.$router.push('/login');
                    this.logoutLoading = false;
                });
            } catch (error) {
                console.error('Erreur lors de la deconnexion:', error);
                this.logoutLoading = false;
                this.$router.push('/login');
            }
        },
        handleSearch() {
            if (this.searchQuery.trim()) {
                this.$router.push({
                    path: '/products/search',
                    query: { q: this.searchQuery }
                });
            }
        },
        toggleUserMenu() {
            this.showUserMenu = !this.showUserMenu;
        },
        closeUserMenu() {
            this.showUserMenu = false;
        }
    }
}
</script>

<style scoped>
/* Minimal scoped styles - most styling is done with Tailwind */
</style>
